import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Brain, FileText, Search, Radar, LineChart, Loader2, Plus, Trash2, Sparkles,
  CheckCircle2, XCircle, ExternalLink, RefreshCw, Send, Download, Eye, EyeOff,
} from "lucide-react";
import { Header } from "@/components/Header";
import { api, apiError } from "@/lib/api";

const Stat = ({ label, value, testid }) => (
  <div className="rounded-2xl border border-[var(--line)] bg-white p-5" data-testid={testid}>
    <p className="text-3xl font-semibold text-[var(--ink)]">{value}</p>
    <p className="mt-1 text-sm text-[var(--ink-soft)]">{label}</p>
  </div>
);

const Ready = ({ ok, label }) => (
  <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${ok ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}>
    {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />} {label}
  </span>
);

const TabBtn = ({ active, onClick, icon: Icon, children, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${active ? "bg-[var(--ink)] text-white" : "text-[var(--ink-soft)] hover:bg-[var(--surface-2)]"}`}>
    <Icon className="h-4 w-4" strokeWidth={1.75} />{children}
  </button>
);

const Btn = ({ onClick, children, testid, disabled, variant = "primary", className = "" }) => {
  const styles = {
    primary: "bg-[var(--brand)] text-white hover:bg-[var(--brand-dark)]",
    ghost: "border border-[var(--line)] bg-white hover:bg-[var(--surface-2)]",
    dark: "bg-[var(--ink)] text-white hover:opacity-90",
  }[variant];
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testid}
      className={`inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-50 ${styles} ${className}`}>
      {children}
    </button>
  );
};

export default function MasonBrain() {
  const [tab, setTab] = useState("overview");
  const [ov, setOv] = useState(null);

  const loadOverview = () => api.get("/seo/overview").then(({ data }) => setOv(data)).catch(() => {});
  useEffect(() => { loadOverview(); }, []);

  return (
    <div className="min-h-screen bg-[var(--bone)]">
      <Header />
      <main className="mx-auto max-w-6xl px-6 py-10 md:px-10">
        <p className="overline flex items-center gap-2"><Brain className="h-4 w-4 text-[var(--brand)]" /> Autonomous SEO / AEO Engine</p>
        <h1 className="mt-2 font-display text-3xl tracking-tight md:text-4xl">Brain</h1>
        <p className="mt-2 text-[var(--ink-soft)]">Roger drafts SEO articles, discovers keywords, probes AI assistants, and reads your Search Console.</p>

        {ov && (
          <div className="mt-6 flex flex-wrap gap-2">
            <Ready ok={ov.tavily_ready} label="Tavily (research)" />
            <Ready ok={ov.perplexity_ready} label="Perplexity (probe)" />
            <Ready ok={ov.gsc_ready} label="Search Console" />
          </div>
        )}

        <div className="mt-6 flex flex-wrap gap-2">
          <TabBtn active={tab === "overview"} onClick={() => setTab("overview")} icon={LineChart} testid="tab-overview">Overview</TabBtn>
          <TabBtn active={tab === "journal"} onClick={() => setTab("journal")} icon={FileText} testid="tab-journal">Journal</TabBtn>
          <TabBtn active={tab === "keywords"} onClick={() => setTab("keywords")} icon={Search} testid="tab-keywords">Keywords</TabBtn>
          <TabBtn active={tab === "probes"} onClick={() => setTab("probes")} icon={Radar} testid="tab-probes">Citation Probe</TabBtn>
          <TabBtn active={tab === "gsc"} onClick={() => setTab("gsc")} icon={LineChart} testid="tab-gsc">Search Console</TabBtn>
        </div>

        <div className="mt-8">
          {tab === "overview" && <Overview ov={ov} reload={loadOverview} />}
          {tab === "journal" && <JournalAdmin reload={loadOverview} />}
          {tab === "keywords" && <Keywords reload={loadOverview} />}
          {tab === "probes" && <Probes />}
          {tab === "gsc" && <GscPanel reload={loadOverview} />}
        </div>
      </main>
    </div>
  );
}

function Overview({ ov, reload }) {
  const [saving, setSaving] = useState(false);
  if (!ov) return <p className="text-[var(--ink-soft)]">Loading…</p>;

  const toggleAuto = async () => {
    setSaving(true);
    try {
      await api.patch("/seo/settings", { auto_blog_enabled: !ov.auto_blog_enabled });
      toast.success(!ov.auto_blog_enabled ? "Auto-drafting enabled" : "Auto-drafting paused");
      reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSaving(false);
  };
  const setFreq = async (mins, label) => {
    try { await api.patch("/seo/settings", { frequency_minutes: mins }); toast.success(`Every ${label}`); reload(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const togglePublish = async () => {
    try {
      await api.patch("/seo/settings", { auto_publish: !ov.auto_publish });
      toast.success(!ov.auto_publish ? "Auto-publish ON — Roger will publish on its own" : "Auto-publish off — drafts only");
      reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        <Stat label="Published" value={ov.published} testid="stat-published" />
        <Stat label="Drafts" value={ov.drafts} testid="stat-drafts" />
        <Stat label="Keyword backlog" value={ov.backlog} testid="stat-backlog" />
        <Stat label="Probes run" value={ov.probes} testid="stat-probes" />
        <Stat label="AI citation rate" value={`${ov.citation_rate}%`} testid="stat-citation" />
      </div>

      <div className="rounded-2xl border border-[var(--line)] bg-white p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-semibold">Autonomous drafting</p>
            <p className="mt-1 text-sm text-[var(--ink-soft)]">
              When on, Roger writes one article from the keyword backlog on your schedule.{" "}
              {ov.auto_publish ? "It publishes automatically — no approval needed." : "Articles wait as drafts for you to approve."}
            </p>
          </div>
          <Btn onClick={toggleAuto} disabled={saving} testid="toggle-auto" variant={ov.auto_blog_enabled ? "dark" : "primary"}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : ov.auto_blog_enabled ? <><EyeOff className="h-4 w-4" /> Pause auto-drafting</> : <><Sparkles className="h-4 w-4" /> Enable auto-drafting</>}
          </Btn>
        </div>
        {ov.auto_blog_enabled && (
          <div className="mt-5 space-y-4 border-t border-[var(--line)] pt-5">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-[var(--ink-soft)]">Publish every</span>
              {[[360, "6h"], [720, "12h"], [1440, "24h"], [2880, "48h"], [671, "11h 11m"]].map(([m, label]) => (
                <button key={m} onClick={() => setFreq(m, label)} data-testid={`freq-${m}`}
                  className={`rounded-full px-3 py-1 text-xs font-semibold ${ov.frequency_minutes === m ? "bg-[var(--brand)] text-white" : "border border-[var(--line)]"}`}>{label}</button>
              ))}
            </div>
            <p className="text-xs text-[var(--ink-soft)]">
              Current cadence: <span className="font-semibold text-[var(--ink)]">every {Math.floor(ov.frequency_minutes / 60)}h {ov.frequency_minutes % 60}m</span>
              {ov.last_auto_run && <> · last run {new Date(ov.last_auto_run).toLocaleString()}</>}
            </p>
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-[var(--surface-2)] p-4">
              <div>
                <p className="text-sm font-semibold">Auto-publish</p>
                <p className="text-xs text-[var(--ink-soft)]">{ov.auto_publish ? "Roger publishes new articles automatically — no approval needed." : "New articles are saved as drafts for you to approve."}</p>
              </div>
              <Btn onClick={togglePublish} testid="toggle-publish" variant={ov.auto_publish ? "dark" : "primary"} className="!px-3 !py-1.5 !text-xs">
                {ov.auto_publish ? <><EyeOff className="h-3.5 w-3.5" /> Turn off auto-publish</> : <><Eye className="h-3.5 w-3.5" /> Turn on auto-publish</>}
              </Btn>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function JournalAdmin({ reload }) {
  const [posts, setPosts] = useState(null);
  const [gen, setGen] = useState(false);
  const [kw, setKw] = useState("");

  const load = () => api.get("/seo/blog/posts").then(({ data }) => setPosts(data)).catch(() => setPosts([]));
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setGen(true);
    try {
      const { data } = await api.post("/seo/blog/generate", { keyword: kw.trim() || undefined });
      toast.success(`Roger drafted: ${data.title}`);
      setKw(""); load(); reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setGen(false);
  };
  const setStatus = async (p, status) => {
    try { await api.patch(`/seo/blog/posts/${p.id}`, { status }); toast.success(status === "published" ? "Published" : "Unpublished"); load(); reload(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try { await api.delete(`/seo/blog/posts/${p.id}`); load(); reload(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-[var(--line)] bg-white p-6">
        <p className="font-semibold">Have Roger write an article</p>
        <p className="mt-1 text-sm text-[var(--ink-soft)]">Leave blank to pull the next keyword from your backlog. Grounded with live web research.</p>
        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
          <input value={kw} onChange={(e) => setKw(e.target.value)} data-testid="generate-keyword-input"
            placeholder="Optional: target keyword (e.g. AI siding visualizer)"
            className="flex-1 rounded-full border border-[var(--line)] px-4 py-2 text-sm outline-none focus:border-[var(--brand)]" />
          <Btn onClick={generate} disabled={gen} testid="generate-article-btn">
            {gen ? <><Loader2 className="h-4 w-4 animate-spin" /> Writing…</> : <><Sparkles className="h-4 w-4" /> Generate draft</>}
          </Btn>
        </div>
      </div>

      {posts === null ? <p className="text-[var(--ink-soft)]">Loading…</p> : posts.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-[var(--line)] p-8 text-center text-[var(--ink-soft)]">No articles yet — generate your first one above.</p>
      ) : (
        <div className="space-y-3">
          {posts.map((p) => (
            <div key={p.id} data-testid={`post-row-${p.slug}`} className="flex flex-col gap-3 rounded-2xl border border-[var(--line)] bg-white p-5 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${p.status === "published" ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"}`}>{p.status}</span>
                  <span className="truncate font-semibold">{p.title}</span>
                </div>
                <p className="mt-1 truncate text-sm text-[var(--ink-soft)]">{p.keyword} · {(p.sections || []).length} sections</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {p.status === "published" && <a href={`/journal/${p.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full border border-[var(--line)] px-3 py-1.5 text-xs font-semibold hover:bg-[var(--surface-2)]"><ExternalLink className="h-3.5 w-3.5" /> View</a>}
                {p.status !== "published"
                  ? <Btn onClick={() => setStatus(p, "published")} testid={`publish-${p.slug}`} className="!px-3 !py-1.5 !text-xs"><Eye className="h-3.5 w-3.5" /> Publish</Btn>
                  : <Btn onClick={() => setStatus(p, "draft")} variant="ghost" testid={`unpublish-${p.slug}`} className="!px-3 !py-1.5 !text-xs"><EyeOff className="h-3.5 w-3.5" /> Unpublish</Btn>}
                <button onClick={() => del(p)} data-testid={`delete-${p.slug}`} className="inline-flex items-center rounded-full border border-[var(--line)] px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Keywords({ reload }) {
  const [kws, setKws] = useState(null);
  const [seed, setSeed] = useState("");
  const [nw, setNw] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/seo/keywords").then(({ data }) => setKws(data)).catch(() => setKws([]));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!nw.trim()) return;
    try { await api.post("/seo/keywords", { keyword: nw.trim() }); setNw(""); load(); reload(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const discover = async () => {
    if (!seed.trim()) return;
    setBusy(true);
    try { const { data } = await api.post("/seo/discover", { seed: seed.trim() }); toast.success(`Added ${data.count} keyword ideas`); setSeed(""); load(); reload(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  const del = async (id) => { await api.delete(`/seo/keywords/${id}`); load(); reload(); };

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-[var(--line)] bg-white p-6">
        <p className="font-semibold">Discover keywords with Roger</p>
        <p className="mt-1 text-sm text-[var(--ink-soft)]">Enter a topic; Roger researches the web and proposes global SaaS keywords.</p>
        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
          <input value={seed} onChange={(e) => setSeed(e.target.value)} data-testid="discover-seed-input"
            placeholder="e.g. bathroom remodel visualization"
            className="flex-1 rounded-full border border-[var(--line)] px-4 py-2 text-sm outline-none focus:border-[var(--brand)]" />
          <Btn onClick={discover} disabled={busy} testid="discover-btn">
            {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Researching…</> : <><Sparkles className="h-4 w-4" /> Discover</>}
          </Btn>
        </div>
      </div>

      <div className="rounded-2xl border border-[var(--line)] bg-white p-6">
        <div className="flex flex-col gap-3 sm:flex-row">
          <input value={nw} onChange={(e) => setNw(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} data-testid="add-keyword-input"
            placeholder="Add a keyword manually"
            className="flex-1 rounded-full border border-[var(--line)] px-4 py-2 text-sm outline-none focus:border-[var(--brand)]" />
          <Btn onClick={add} variant="ghost" testid="add-keyword-btn"><Plus className="h-4 w-4" /> Add</Btn>
        </div>
        {kws === null ? <p className="mt-4 text-[var(--ink-soft)]">Loading…</p> : kws.length === 0 ? (
          <p className="mt-4 text-sm text-[var(--ink-soft)]">No keywords yet.</p>
        ) : (
          <div className="mt-4 flex flex-wrap gap-2">
            {kws.map((k) => (
              <span key={k.id} data-testid={`kw-${k.id}`} className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium ${k.status === "used" ? "bg-[var(--surface-2)] text-[var(--ink-soft)] line-through" : "border border-[var(--line)]"}`}>
                {k.keyword}
                <span className="rounded-full bg-[var(--brand)]/10 px-1.5 text-[10px] font-semibold text-[var(--brand)]">{k.source}</span>
                <button onClick={() => del(k.id)} className="text-[var(--ink-soft)] hover:text-red-600"><Trash2 className="h-3 w-3" /></button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Probes() {
  const [q, setQ] = useState("What is the best AI before and after visualization software for home improvement contractors?");
  const [busy, setBusy] = useState(false);
  const [probes, setProbes] = useState(null);

  const load = () => api.get("/seo/probes").then(({ data }) => setProbes(data)).catch(() => setProbes([]));
  useEffect(() => { load(); }, []);

  const run = async () => {
    if (!q.trim()) return;
    setBusy(true);
    try { const { data } = await api.post("/seo/probe", { query: q.trim() }); toast[data.mentioned ? "success" : "message"](data.mentioned ? "Customize Studio was mentioned!" : "Not mentioned — citation gap"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-[var(--line)] bg-white p-6">
        <p className="font-semibold">Probe AI assistants for citation gaps</p>
        <p className="mt-1 text-sm text-[var(--ink-soft)]">Ask what AI assistants recommend and see whether Customize Studio shows up (and who does).</p>
        <div className="mt-4 flex flex-col gap-3">
          <textarea value={q} onChange={(e) => setQ(e.target.value)} data-testid="probe-input" rows={2}
            className="w-full rounded-2xl border border-[var(--line)] px-4 py-3 text-sm outline-none focus:border-[var(--brand)]" />
          <Btn onClick={run} disabled={busy} testid="probe-btn" className="self-start">
            {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Probing…</> : <><Send className="h-4 w-4" /> Run probe</>}
          </Btn>
        </div>
      </div>

      {probes === null ? <p className="text-[var(--ink-soft)]">Loading…</p> : probes.map((p) => (
        <div key={p.id} data-testid="probe-result" className="rounded-2xl border border-[var(--line)] bg-white p-5">
          <div className="flex items-start justify-between gap-3">
            <p className="font-semibold">{p.query}</p>
            <span className={`flex-none rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${p.mentioned ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}>{p.mentioned ? "Mentioned" : "Gap"}</span>
          </div>
          <div className="mt-3 space-y-3">
            {(p.results || []).map((r, i) => (
              <div key={i} className="rounded-xl bg-[var(--surface-2)] p-3">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[var(--ink-soft)]">
                  {r.provider}
                  {!r.error && <span className={r.mentioned ? "text-green-700" : "text-red-600"}>· {r.mentioned ? "mentions us" : "no mention"}</span>}
                </div>
                {r.error ? <p className="mt-1 text-sm text-red-600">{r.error}</p> : (
                  <>
                    <p className="mt-1 line-clamp-4 text-sm text-[var(--ink-soft)]">{r.answer}</p>
                    {r.competitors && r.competitors.length > 0 && (
                      <p className="mt-2 text-xs text-[var(--ink-soft)]">Cited competitors: {r.competitors.join(", ")}</p>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
          <p className="mt-2 text-[11px] text-[var(--ink-soft)]">{new Date(p.created_at).toLocaleString()}</p>
        </div>
      ))}
    </div>
  );
}

function GscPanel({ reload }) {
  const [rows, setRows] = useState(null);
  const [days, setDays] = useState(28);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async (d = days) => {
    setBusy(true); setErr("");
    try { const { data } = await api.get(`/seo/gsc/top-queries?days=${d}`); setRows(data.rows); }
    catch (e) { setErr(apiError(e.response?.data?.detail)); setRows([]); }
    setBusy(false);
  };
  useEffect(() => { load(28); /* eslint-disable-next-line */ }, []);

  const importKw = async () => {
    try { const { data } = await api.post("/seo/gsc/import-keywords", { days, min_impressions: 10 }); toast.success(`Imported ${data.count} keywords`); reload(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-2">
        {[7, 28, 90].map((d) => (
          <button key={d} onClick={() => { setDays(d); load(d); }} data-testid={`gsc-days-${d}`}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold ${days === d ? "bg-[var(--brand)] text-white" : "border border-[var(--line)]"}`}>Last {d}d</button>
        ))}
        <Btn onClick={() => load()} variant="ghost" testid="gsc-refresh"><RefreshCw className="h-4 w-4" /> Refresh</Btn>
        <Btn onClick={importKw} testid="gsc-import"><Download className="h-4 w-4" /> Import as keywords</Btn>
      </div>

      {err && <p className="rounded-xl bg-red-50 p-4 text-sm text-red-600">{err}</p>}
      {busy ? <p className="text-[var(--ink-soft)]">Loading…</p> : rows && rows.length === 0 && !err ? (
        <p className="rounded-2xl border border-dashed border-[var(--line)] p-8 text-center text-[var(--ink-soft)]">No Search Console data yet for this range. New properties take a few days to collect data.</p>
      ) : rows && rows.length > 0 && (
        <div className="overflow-x-auto rounded-2xl border border-[var(--line)] bg-white">
          <table className="w-full text-sm">
            <thead className="border-b border-[var(--line)] text-left text-xs uppercase tracking-wide text-[var(--ink-soft)]">
              <tr><th className="px-4 py-3">Query</th><th className="px-4 py-3">Clicks</th><th className="px-4 py-3">Impressions</th><th className="px-4 py-3">CTR</th><th className="px-4 py-3">Position</th></tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b border-[var(--line)] last:border-0">
                  <td className="px-4 py-2.5 font-medium">{r.query}</td>
                  <td className="px-4 py-2.5">{r.clicks}</td>
                  <td className="px-4 py-2.5">{r.impressions}</td>
                  <td className="px-4 py-2.5">{(r.ctr * 100).toFixed(1)}%</td>
                  <td className="px-4 py-2.5">{r.position.toFixed(1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
